// Tiny dependency-free SVG sparkline / bar chart helpers
export function Sparkline({ data, height = 48, stroke = "#0A1F1C", fill = "rgba(232,255,58,0.25)" }) {
  if (!data || data.length === 0) return null;
  const w = 200;
  const max = Math.max(...data.map(d => d.value ?? d.score ?? d.amount ?? 0), 1);
  const min = Math.min(...data.map(d => d.value ?? d.score ?? d.amount ?? 0), 0);
  const range = max - min || 1;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * w;
    const v = d.value ?? d.score ?? d.amount ?? 0;
    const y = height - ((v - min) / range) * (height - 8) - 4;
    return [x, y];
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${w},${height} L0,${height} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full h-full" preserveAspectRatio="none">
      <path d={area} fill={fill} />
      <path d={line} stroke={stroke} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      {pts.length > 0 && <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r="3" fill={stroke}/>}
    </svg>
  );
}

export function BarChart({ data, height = 140, accent = "#0A1F1C", stackColor = "#E8FF3A" }) {
  if (!data || data.length === 0) return null;
  // Stacked mode kicks in when items expose both `paid` and `outstanding`.
  // In that case `amount` is the total (paid + outstanding) used for bar height,
  // and we render two segments per bar.
  const isStacked = data.every(d => d && d.paid !== undefined && d.outstanding !== undefined);
  const max = Math.max(...data.map(d => d.amount ?? d.value ?? 0), 1);
  return (
    <div className="flex items-end gap-2 w-full" style={{ height }}>
      {data.map((d, i) => {
        const total = d.amount ?? d.value ?? 0;
        const h = (total / max) * (height - 24);
        const paidPct = isStacked && total > 0 ? (d.paid / total) : 1;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="text-[10px] mono text-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity">
              {isStacked
                ? `$${Math.round(d.paid).toLocaleString()} paid · $${Math.round(d.outstanding).toLocaleString()} open`
                : `$${Math.round(total).toLocaleString()}`}
            </div>
            <div className="w-full rounded-t-md transition-all overflow-hidden flex flex-col-reverse"
                 style={{ height: Math.max(h, 2), background: isStacked ? stackColor : accent, opacity: isStacked ? 1 : 0.3 + (i / data.length) * 0.7 }}>
              {isStacked && (
                <div style={{ height: `${paidPct * 100}%`, background: accent, width: "100%" }} />
              )}
            </div>
            <div className="text-[10px] text-foreground/50">{d.month ?? d.label}</div>
          </div>
        );
      })}
    </div>
  );
}

export function Donut({ value = 0, max = 100, size = 120, stroke = 10, color = "#0A1F1C", track = "rgba(10,31,28,0.08)" }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(1, Math.max(0, value / max));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={track} strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
        transform={`rotate(-90 ${size/2} ${size/2})`}/>
    </svg>
  );
}
